import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  Bell,
  Settings,
  Printer,
  Star,
  Calendar,
  Share2,
  Activity,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Box,
  Phone,
  MessageSquare,
  ArrowUpRight,
  ArrowUp,
  ArrowDown,
  Dumbbell,
  Ruler,
  User,
  Droplet,
  LayoutDashboard,
  Users,
  CalendarDays,
  FlaskConical,
  Microscope,
  ScanLine,
  FileSignature,
  Plus,
  ChevronDown,
  Brain,
  Stethoscope,
  ShieldAlert,
  Server,
  Sparkles,
  AlertTriangle,
  Cpu,
  Wallet,
  Database,
  KeyRound,
  BarChart3,
  type LucideIcon,
} from "lucide-react";
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
} from "recharts";
import logoUrl from "@/assets/logo-dikdoc.svg";
import AgendaView from "@/components/views/AgendaView";
import LaboratoireView from "@/components/views/LaboratoireView";
import ImagerieView from "@/components/views/ImagerieView";
import PatientsView from "@/components/views/PatientsView";
import ENotesView from "@/components/views/ENotesView";
import ConfigurationView from "@/components/views/ConfigurationView";
import SuperAdminView from "@/components/views/SuperAdminView";
import ClinicalOpsCenterView from "@/components/views/ClinicalOpsCenterView";
import OperationsCenterView from "@/components/views/OperationsCenterView";
import PlatformGovernanceCenterView from "@/components/views/PlatformGovernanceCenterView";
import SecurityOpsCenterView from "@/components/views/SecurityOpsCenterView";
import PostgresMetricsView from "@/components/views/PostgresMetricsView";
import KubernetesMetricsView from "@/components/views/KubernetesMetricsView";
import RedisMetricsView from "@/components/views/RedisMetricsView";
import KafkaMetricsView from "@/components/views/KafkaMetricsView";
import DoctorsView from "@/components/views/DoctorsView";
import PharmacyView from "@/components/views/PharmacyView";
import CotationsView from "@/components/views/CotationsView";
import IdentityAccessView from "@/components/views/IdentityAccessView";
import NeuroCommandCenter from "@/components/views/Neuro/NeuroCommandCenter";
import MinistryCommandCenter from "@/components/views/MinistryCommandCenter/MinistryCommandCenter";
import MachinesView from "@/components/views/MachinesView";
import ConsultationsAtomiquesView from "@/components/views/ConsultationsAtomiquesView";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "DikDoc — Opérations de Santé" },
      {
        name: "description",
        content:
          "Tableau de bord clinique DikDoc : indicateurs en temps réel pour piloter les opérations hospitalières.",
      },
      { property: "og:title", content: "DikDoc — Opérations de Santé" },
      {
        property: "og:description",
        content: "Pilotez vos opérations cliniques en temps réel.",
      },
    ],
  }),
  component: Dashboard,
});

/* ------------------------------- MENU BAR ------------------------------- */

type MenuItem = { id: string; label: string; icon: LucideIcon };

const MENU: MenuItem[] = [
  { id: "dashboard", label: "Tableau de bord", icon: LayoutDashboard },
  { id: "patients", label: "Patients", icon: Users },
  { id: "agenda", label: "Agenda", icon: CalendarDays },
  { id: "laboratoire", label: "Laboratoire", icon: FlaskConical },
  { id: "imagerie", label: "Imagerie", icon: ScanLine },
  { id: "enotes", label: "E-notes", icon: FileSignature },
  { id: "config", label: "Configuration", icon: Settings },
];

function UnifiedMenu({ active, setActive }: { active: string; setActive: (id: string) => void }) {
  return (
    <div className="card-surface flex items-center gap-1 rounded-full px-2 py-1.5">
      {MENU.map((m) => {
        const Icon = m.icon;
        const isActive = active === m.id;
        return (
          <button
            key={m.id}
            onClick={() => setActive(m.id)}
            title={m.label}
            className={
              "group relative flex items-center gap-2 rounded-full px-3 py-2 text-sm font-medium transition-all duration-300 " +
              (isActive
                ? "bg-foreground text-background shadow-[0_8px_24px_-12px_oklch(1_0_0/0.6)]"
                : "text-muted-foreground hover:text-foreground hover:bg-white/5")
            }
          >
            <Icon className="h-4 w-4 shrink-0" strokeWidth={1.8} />
            <span
              className={
                "overflow-hidden whitespace-nowrap transition-all duration-300 " +
                (isActive
                  ? "max-w-[160px] opacity-100"
                  : "max-w-0 opacity-0 group-hover:max-w-[160px] group-hover:opacity-100")
              }
            >
              {m.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* ------------------------------ MOCK DATA ------------------------------- */

const spark = (seed: number) =>
  Array.from({ length: 14 }, (_, i) => ({
    x: i,
    y: 30 + Math.sin(i / 1.4 + seed) * 14 + Math.cos(i / 2.2 + seed) * 8 + seed * 3,
  }));

/* -------------------------------- CARDS --------------------------------- */

function StatCard({
  label,
  value,
  delta,
  deltaTone,
  data,
  tone = "neutral",
}: {
  label: string;
  value: string;
  delta: string;
  deltaTone: "up" | "down";
  data: { x: number; y: number }[];
  tone?: "neutral" | "success" | "danger";
}) {
  const stroke =
    tone === "success"
      ? "oklch(0.78 0.18 150)"
      : tone === "danger"
        ? "oklch(0.7 0.2 35)"
        : "oklch(0.7 0.02 240)";
  const fill =
    tone === "success"
      ? "oklch(0.78 0.18 150 / 0.35)"
      : tone === "danger"
        ? "oklch(0.7 0.2 35 / 0.25)"
        : "oklch(0.7 0.02 240 / 0.25)";
  const gid = `g-${label.replace(/\s/g, "")}`;
  return (
    <div className="card-surface relative overflow-hidden rounded-2xl p-5">
      <div className="flex items-start justify-between">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span
          className={
            "inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium " +
            (deltaTone === "up"
              ? "bg-success/15 text-success"
              : "bg-accent/15 text-accent")
          }
        >
          {deltaTone === "up" ? (
            <ArrowUp className="h-3 w-3" />
          ) : (
            <ArrowDown className="h-3 w-3" />
          )}
          {delta}
        </span>
      </div>
      <div className="mt-8 flex items-end justify-between gap-4">
        <div className="text-5xl font-light tracking-tight">{value}</div>
        <div className="h-14 w-1/2">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={fill} />
                  <stop offset="100%" stopColor="transparent" />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="y"
                stroke={stroke}
                strokeWidth={1.5}
                fill={`url(#${gid})`}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function PatientAppointmentCard() {
  return (
    <div className="card-surface rounded-2xl p-5">
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>Patients & Rendez-vous</span>
        <Maximize2 className="h-4 w-4" />
      </div>
      <div className="mt-6 text-center text-6xl font-light">214</div>
      <div className="mt-6 flex items-end justify-between text-xs">
        <div>
          <div className="text-2xl font-light">138</div>
          <div className="text-muted-foreground">Patients</div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-light">76</div>
          <div className="text-muted-foreground">Rendez-vous</div>
        </div>
      </div>
      <div className="mt-3 flex h-3 overflow-hidden rounded-full bg-white/5">
        <div
          className="h-full rounded-full bg-gradient-to-r from-success/40 via-success to-success/80"
          style={{ width: "64%" }}
        />
        <div
          className="h-full ml-0.5 flex-1 bg-[repeating-linear-gradient(90deg,oklch(1_0_0/0.25)_0_2px,transparent_2px_4px)]"
        />
      </div>
    </div>
  );
}

function ServiceTypeCard() {
  const data = [{ name: "v", value: 75, fill: "oklch(0.78 0.18 150)" }];
  return (
    <div className="card-surface rounded-2xl p-5">
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>Type de Service</span>
        <Maximize2 className="h-4 w-4" />
      </div>
      <div className="relative mt-2 h-44">
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart
            innerRadius="78%"
            outerRadius="100%"
            startAngle={210}
            endAngle={-30}
            data={data}
          >
            <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
            <RadialBar background={{ fill: "oklch(1 0 0 / 0.06)" }} dataKey="value" cornerRadius={20} />
          </RadialBarChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-xs text-muted-foreground">
            <span className="text-base text-foreground font-medium">358</span> Spécialistes
          </div>
          <div className="mt-1 text-4xl font-light">469</div>
          <div className="text-xs text-muted-foreground">Total</div>
        </div>
      </div>
    </div>
  );
}

function StaffAvailability() {
  const staff = [
    { name: "Dr. Andrew Smith", role: "Cardiologue", online: true },
    { name: "Dr. Marie Dubois", role: "Neurologue", online: true },
    { name: "Dr. Karim El Idrissi", role: "Radiologue", online: false },
  ];
  return (
    <div className="card-surface rounded-2xl p-5">
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>Disponibilité du Personnel</span>
        <Maximize2 className="h-4 w-4" />
      </div>
      <ul className="mt-4 space-y-3">
        {staff.map((s, i) => (
          <li key={i} className="flex items-center gap-3">
            <div className="relative">
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-gradient-to-br from-success/40 to-accent/40 text-sm font-medium">
                {s.name.split(" ")[1]?.[0]}
                {s.name.split(" ")[2]?.[0]}
              </div>
              <span
                className={
                  "absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-card " +
                  (s.online ? "bg-success" : "bg-muted-foreground")
                }
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm">{s.name}</div>
              <div className="truncate text-xs text-muted-foreground">{s.role}</div>
            </div>
            <button className="grid h-8 w-8 place-items-center rounded-full bg-white/5 text-muted-foreground hover:text-foreground">
              <Phone className="h-3.5 w-3.5" />
            </button>
            <button className="grid h-8 w-8 place-items-center rounded-full bg-white/5 text-muted-foreground hover:text-foreground">
              <MessageSquare className="h-3.5 w-3.5" />
            </button>
            <button className="grid h-8 w-8 place-items-center rounded-full bg-accent text-accent-foreground">
              <ArrowUpRight className="h-3.5 w-3.5" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function AIInsights() {
  return (
    <div className="card-surface relative overflow-hidden rounded-2xl p-5">
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>Aperçus IA</span>
        <Activity className="h-4 w-4 text-success" />
      </div>
      <p className="mt-4 text-sm leading-relaxed">
        Les <span className="font-medium">rendez-vous manqués</span> ont
        <span className="text-accent"> augmenté de 12&nbsp;%</span> par rapport à
        la semaine dernière.
      </p>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
        Recommandation : activer les rappels SMS 24h avant.
      </p>
      <div className="mt-5 h-10 w-full">
        <svg viewBox="0 0 200 40" className="h-full w-full">
          {Array.from({ length: 60 }).map((_, i) => (
            <line
              key={i}
              x1={i * 3.4}
              y1={20 + (Math.sin(i) * 14)}
              x2={i * 3.4}
              y2={20 - (Math.cos(i / 1.5) * 12)}
              stroke="oklch(1 0 0 / 0.35)"
              strokeWidth="1"
            />
          ))}
        </svg>
      </div>
    </div>
  );
}

function PatientCard() {
  return (
    <div className="card-surface flex flex-col gap-4 rounded-2xl p-4 sm:flex-row sm:items-stretch">
      <div className="relative aspect-square w-full overflow-hidden rounded-xl sm:w-44">
        <img
          src="https://images.unsplash.com/photo-1607746882042-944635dfe10e?auto=format&fit=crop&w=600&q=80"
          alt="James Miamai"
          className="h-full w-full object-cover"
        />
        <div className="absolute left-3 top-3 flex flex-col gap-2">
          <button className="grid h-8 w-8 place-items-center rounded-full bg-black/50 backdrop-blur text-foreground">
            <Phone className="h-3.5 w-3.5" />
          </button>
          <button className="grid h-8 w-8 place-items-center rounded-full bg-black/50 backdrop-blur text-foreground">
            <MessageSquare className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <div className="flex-1">
        <div className="text-2xl font-medium">James Miamai</div>
        <div className="text-sm text-muted-foreground">Homme, 35 ans</div>
        <div className="mt-5 grid grid-cols-2 gap-4">
          <div className="flex items-center gap-3">
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-lg bg-accent text-accent-foreground">
              <Dumbbell className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Poids</div>
              <div className="text-2xl font-light">67kg</div>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Ruler className="h-4 w-4 text-muted-foreground" />
              <div>
                <div className="text-xs text-muted-foreground">Taille</div>
                <div className="text-sm">1.67m</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <User className="h-4 w-4 text-muted-foreground" />
              <div>
                <div className="text-xs text-muted-foreground">IMC</div>
                <div className="text-sm">23.0</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Droplet className="h-4 w-4 text-muted-foreground" />
              <div>
                <div className="text-xs text-muted-foreground">Groupe sanguin</div>
                <div className="text-sm">O+</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ScanPanel() {
  const [tab, setTab] = useState("Cancer du cerveau");
  const tabs = ["Cancer du cerveau", "Maladie cardiaque", "Infection sévère"];
  return (
    <div className="card-surface rounded-2xl p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {tabs.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={"chip " + (tab === t ? "chip-active" : "")}
            >
              {t}
            </button>
          ))}
        </div>
        <div className="inline-flex items-center gap-2 text-sm text-success">
          <Activity className="h-4 w-4" />
          Données hospitalières en direct
        </div>
      </div>

      <div className="mt-6 grid grid-cols-[64px_1fr_28px] items-center gap-4">
        <div className="flex flex-col gap-3">
          {[0, 1, 2].map((i) => (
            <button
              key={i}
              className={
                "aspect-square w-16 overflow-hidden rounded-lg border " +
                (i === 1 ? "border-foreground" : "border-transparent opacity-70")
              }
            >
              <img
                src="https://images.unsplash.com/photo-1559757175-5700dde675bc?auto=format&fit=crop&w=200&q=60"
                alt="aperçu scan"
                className="h-full w-full object-cover"
              />
            </button>
          ))}
        </div>

        <div className="relative mx-auto aspect-square w-full max-w-[360px]">
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-success/10 via-transparent to-accent/10 blur-2xl" />
          <div className="absolute inset-0 grid place-items-center rounded-full border border-white/10 bg-black/40 backdrop-blur">
            <img
              src="https://images.unsplash.com/photo-1559757148-5c350d0d3c56?auto=format&fit=crop&w=600&q=80"
              alt="Imagerie cérébrale"
              className="h-[78%] w-[78%] rounded-full object-cover opacity-90 mix-blend-screen"
            />
          </div>
          <div className="absolute left-1/2 top-4 flex -translate-x-1/2 gap-2">
            <button className="grid h-9 w-9 place-items-center rounded-full bg-white/5 text-muted-foreground">
              <ZoomIn className="h-4 w-4" />
            </button>
            <button className="grid h-9 w-9 place-items-center rounded-full bg-foreground text-background">
              <ZoomOut className="h-4 w-4" />
            </button>
            <button className="grid h-9 w-9 place-items-center rounded-full bg-white/5 text-muted-foreground">
              <Box className="h-4 w-4" />
            </button>
            <button className="grid h-9 w-9 place-items-center rounded-full bg-white/5 text-muted-foreground">
              <Maximize2 className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="flex h-56 flex-col items-center justify-between text-[10px] text-muted-foreground">
          <span>0.50</span>
          <div className="my-1 h-full w-2 rounded-full bg-gradient-to-b from-[oklch(0.78_0.18_150)] via-[oklch(0.85_0.18_85)] to-[oklch(0.65_0.22_27)]" />
          <span>0.10</span>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------- LAYOUT --------------------------------- */

const TITLES: Record<string, { title: string; subtitle: string }> = {
  dashboard: { title: "Opérations de Santé", subtitle: "Insights actionnables pour soutenir la décision clinique et opérationnelle." },
  patients: { title: "Patients", subtitle: "Suivi clinique et administratif de votre patientèle." },
  agenda: { title: "Agenda", subtitle: "Planification des rendez-vous et disponibilités du personnel." },
  laboratoire: { title: "Laboratoire", subtitle: "Résultats biologiques et indicateurs de longévité." },
  imagerie: { title: "Imagerie", subtitle: "Examens d'imagerie médicale et analyses associées." },
  enotes: { title: "E-notes", subtitle: "Notes cliniques numériques et comptes-rendus." },
  config: { title: "Configuration", subtitle: "Paramètres du cabinet et préférences." },
  superadmin: { title: "Cockpit SuperAdmin", subtitle: "Vue d'ensemble santé, sécurité, plateforme et IA." },
  clinicalops: { title: "Clinical Operations Center", subtitle: "Patients · Soins · Labo · Imagerie · Pharmacie · Facturation." },
  operations: { title: "Operations Center", subtitle: "Lits, personnel, stocks et équipements en temps réel." },
  governance: { title: "Platform Governance Center", subtitle: "IAM, compliance, audit, vault, infrastructure et monitoring." },
  security: { title: "Security Operations Center", subtitle: "Threats, audit chain, IoMT, logs cluster et réponse aux incidents." },
  postgres: { title: "PostgreSQL Metrics", subtitle: "Database · queries · errors · relations — temps réel." },
  kubernetes: { title: "Kubernetes Cluster", subtitle: "Nodes · Namespaces · Microservices · Pods — live." },
  redis: { title: "Redis Metrics", subtitle: "Cache · pub/sub · clients · mémoire — live." },
  kafka: { title: "Strimzi Kafka Dashboard", subtitle: "Brokers · Topics · Partitions · Throughput — live." },
  doctors: { title: "Médecins", subtitle: "Profils, spécialités, NGAP, pharmacie et personnel paramédical." },
  cotations: { title: "Cotations NGAP", subtitle: "Grilles AMO Maroc · codes par spécialité · assignation aux médecins." },
  "pharmacy-stocks": { title: "Pharmacy — Stocks", subtitle: "Niveaux · seuils sécurité · lots & expirations." },
  "pharmacy-dispensation": { title: "Pharmacy — Dispensation", subtitle: "Actes nominatifs · CNSS · stupéfiants." },
  "pharmacy-tracabilite": { title: "Pharmacy — Traçabilité", subtitle: "Track & Trace lot → patient." },
  "pharmacy-vue-patient": { title: "Pharmacy — Vue patient", subtitle: "Dernier patient · recherche par nom et date de naissance." },
  machines: { title: "Configuration machines", subtitle: "Templates · instances · calibration · auto-test · policies." },
  "consultations-atomiques": { title: "Consultations atomiques", subtitle: "Optimistic locking · sagas · résolution de conflits multi-praticien." },
  "ministry-command-center": { title: "Ministry Executive Command Center", subtitle: "Vue nationale — établissements, médicaments, fraude, épidémiologie, rappels." },
};

function ComingSoon({ label }: { label: string }) {
  return (
    <div className="card-surface rounded-2xl p-16 text-center">
      <div className="mx-auto inline-flex items-center gap-2 rounded-full bg-white/5 px-4 py-2 text-xs text-muted-foreground">
        Module {label} — bientôt disponible
      </div>
    </div>
  );
}

/* --------------------------- CENTERS DROPDOWN ---------------------------- */

type CenterEntry = {
  id: string;
  label: string;
  icon: LucideIcon;
  group: "Operations" | "Clinical" | "AI & Data" | "Security";
  badge?: string;
};

const CENTERS: CenterEntry[] = [
  // Operations
  { id: "ministry-command-center", label: "Ministry Executive Command Center", icon: BarChart3, group: "Operations", badge: "NEW" },
  { id: "superadmin", label: "Cockpit SuperAdmin", icon: Settings, group: "Operations" },
  { id: "clinicalops", label: "Clinical Operations Center", icon: Stethoscope, group: "Operations" },
  { id: "operations", label: "Operations Center", icon: Activity, group: "Operations" },
  { id: "governance", label: "Platform Governance Center", icon: Server, group: "Operations" },
  { id: "security", label: "Security Operations Center", icon: ShieldAlert, group: "Operations" },
  { id: "identity-access", label: "Identity & Access (IAM)", icon: KeyRound, group: "Operations", badge: "NEW" },
  // Clinical
  { id: "neuro-command-center", label: "Neuro Command Center", icon: Brain, group: "Clinical", badge: "NEW" },
  { id: "machines", label: "Machines / Équipements", icon: Cpu, group: "Clinical" },
  { id: "consultations-atomiques", label: "Consultations Atomiques", icon: Microscope, group: "Clinical" },
  // AI & Data
  { id: "pharmacy-stocks", label: "Pharmacie — Stocks", icon: Wallet, group: "AI & Data" },
  { id: "pharmacy-dispensation", label: "Pharmacie — Dispensation", icon: Sparkles, group: "AI & Data" },
  { id: "pharmacy-tracabilite", label: "Pharmacie — Traçabilité", icon: Database, group: "AI & Data", badge: "NEW" },
  { id: "pharmacy-vue-patient", label: "Pharmacie — Vue Patient", icon: User, group: "AI & Data" },
  { id: "cotations", label: "Cotations NGAP", icon: Wallet, group: "AI & Data" },
  // Security / infra
  { id: "postgres", label: "Métriques PostgreSQL", icon: Database, group: "Security" },
  { id: "redis", label: "Métriques Redis", icon: Database, group: "Security" },
  { id: "kafka", label: "Métriques Kafka", icon: Database, group: "Security" },
  { id: "kubernetes", label: "Métriques Kubernetes", icon: Server, group: "Security" },
  { id: "doctors", label: "Gestion Praticiens", icon: User, group: "Security" },
];

function CentersMenu({ open, onClose, onSelect, active }: {
  open: boolean;
  onClose: () => void;
  onSelect: (id: string) => void;
  active: string;
}) {
  if (!open) return null;
  const groups = ["Operations", "Clinical", "AI & Data", "Security"] as const;
  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div className="card-surface absolute right-0 top-12 z-50 w-[420px] rounded-2xl p-3 shadow-2xl">
        <div className="mb-2 flex items-center justify-between px-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Centres & Outils
          </span>
          <span className="text-[10px] text-muted-foreground">{CENTERS.length} modules</span>
        </div>
        <div className="max-h-[70vh] overflow-y-auto pr-1">
          {groups.map((g) => {
            const items = CENTERS.filter((c) => c.group === g);
            return (
              <div key={g} className="mb-3">
                <div className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                  {g}
                </div>
                <div className="grid grid-cols-1 gap-1">
                  {items.map((c) => {
                    const Icon = c.icon;
                    const isActive = active === c.id;
                    return (
                      <button
                        key={c.id}
                        onClick={() => { onSelect(c.id); onClose(); }}
                        className={
                          "flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm transition " +
                          (isActive
                            ? "bg-foreground text-background"
                            : "text-foreground hover:bg-white/5")
                        }
                      >
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4" strokeWidth={1.8} />
                          <span>{c.label}</span>
                        </div>
                        {c.badge && (
                          <span className="rounded-full bg-success/20 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-success">
                            {c.badge}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

function Dashboard() {
  const [active, setActive] = useState("dashboard");
  const [centersOpen, setCentersOpen] = useState(false);
  const { title, subtitle } = TITLES[active] ?? TITLES.dashboard;

  return (
    <main className="min-h-screen px-4 py-6 sm:px-8 sm:py-10">
      <div className="mx-auto max-w-[1500px]">
        <div className="card-surface rounded-3xl p-5 sm:p-8">
          {/* HEADER */}
          <header className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 lg:grid-cols-[auto_1fr_auto]">
            <div className="flex items-center gap-3">
              <img src={logoUrl} alt="DikDoc" className="h-11 w-11 shrink-0" />
              <div className="hidden sm:block">
                <div className="text-sm font-semibold leading-none">DikDoc</div>
                <div className="text-xs text-muted-foreground">Cabinet médical</div>
              </div>
            </div>
            <div className="col-span-2 order-3 flex justify-center lg:order-none lg:col-span-1">
              <UnifiedMenu active={active} setActive={setActive} />
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <button
                  onClick={() => setCentersOpen((v) => !v)}
                  aria-label="Centres & Outils"
                  title="Centres & Outils (Neuro, IAM, Imagerie, etc.)"
                  className={
                    "relative grid h-10 w-10 place-items-center rounded-full transition " +
                    (centersOpen
                      ? "bg-foreground text-background"
                      : "bg-white/5 text-muted-foreground hover:text-foreground")
                  }
                >
                  <Plus className="h-4 w-4" />
                </button>
                <CentersMenu
                  open={centersOpen}
                  onClose={() => setCentersOpen(false)}
                  onSelect={setActive}
                  active={active}
                />
              </div>
              <button className="relative grid h-10 w-10 place-items-center rounded-full bg-white/5 text-muted-foreground hover:text-foreground">
                <Bell className="h-4 w-4" />
                <span className="absolute right-2.5 top-2.5 h-1.5 w-1.5 rounded-full bg-accent" />
              </button>
              <button
                onClick={() => setActive("superadmin")}
                aria-label="Cockpit SuperAdmin"
                title="Cockpit SuperAdmin"
                className={
                  "relative grid h-10 w-10 place-items-center rounded-full transition " +
                  (active === "superadmin"
                    ? "bg-foreground text-background"
                    : "bg-white/5 text-muted-foreground hover:text-foreground")
                }
              >
                <Settings className="h-4 w-4" />
                {active === "superadmin" && (
                  <span className="absolute right-2 top-2 h-1.5 w-1.5 rounded-full bg-accent" />
                )}
              </button>
              <div className="flex items-center gap-2 rounded-full bg-white/5 py-1 pl-1 pr-3">
                <div className="grid h-8 w-8 place-items-center overflow-hidden rounded-full bg-accent/20">
                  <img
                    src="https://images.unsplash.com/photo-1633332755192-727a05c4013d?auto=format&fit=crop&w=120&q=60"
                    alt="Rafatar Gord"
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="hidden text-xs leading-tight sm:block">
                  <div className="font-medium">Rafatar Gord</div>
                  <div className="text-muted-foreground">Admin</div>
                </div>
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
              </div>
            </div>
          </header>

          {/* TITLE */}
          <section className="mt-10 grid grid-cols-[minmax(0,1fr)_auto] items-end gap-6">
            <div className="min-w-0">
              {active !== "dashboard" && (
                <button
                  onClick={() => setActive("dashboard")}
                  className="mb-3 inline-flex items-center gap-1 rounded-full bg-white/5 px-3 py-1 text-xs text-muted-foreground transition hover:bg-white/10 hover:text-foreground"
                >
                  ← Retour au tableau de bord
                </button>
              )}
              <h1 className="text-4xl font-light tracking-tight sm:text-5xl lg:text-6xl">
                {title}
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
                {subtitle}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button className="grid h-10 w-10 place-items-center rounded-full bg-white/5 text-muted-foreground hover:text-foreground">
                <Printer className="h-4 w-4" />
              </button>
              <button className="grid h-10 w-10 place-items-center rounded-full bg-white/5 text-muted-foreground hover:text-foreground">
                <Star className="h-4 w-4" />
              </button>
              <button className="hidden sm:inline-flex items-center gap-2 rounded-full bg-white/5 px-4 py-2 text-sm text-foreground">
                <Calendar className="h-4 w-4" />
                Cette semaine
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
              <button className="inline-flex items-center gap-2 rounded-full bg-white/5 py-1 pl-1 pr-3 text-sm">
                <div className="flex -space-x-2">
                  {[
                    "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=80&q=60",
                    "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=80&q=60",
                    "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=80&q=60",
                  ].map((u, i) => (
                    <img
                      key={i}
                      src={u}
                      alt=""
                      className="h-7 w-7 rounded-full border-2 border-card object-cover"
                    />
                  ))}
                </div>
                <Share2 className="h-4 w-4 ml-1" />
                <span className="hidden sm:inline">Partager</span>
              </button>
            </div>
          </section>

          {/* CONTENT */}
          <section className="mt-8">
            {active === "dashboard" && (
              <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                <div className="flex flex-col gap-5">
                  <ScanPanel />
                  <PatientCard />
                </div>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <StatCard label="Total Patients" value="50" delta="6" deltaTone="up" data={spark(1)} />
                  <StatCard label="Rendez-vous" value="45" delta="8" deltaTone="up" data={spark(2)} />
                  <StatCard label="Patients critiques" value="5" delta="7" deltaTone="down" tone="success" data={spark(3)} />
                  <StatCard label="Revenus" value="15k€" delta="765€" deltaTone="down" tone="danger" data={spark(4)} />
                  <PatientAppointmentCard />
                  <ServiceTypeCard />
                  <StaffAvailability />
                  <AIInsights />
                </div>
              </div>
            )}
            {active === "agenda" && <AgendaView />}
            {active === "laboratoire" && <LaboratoireView />}
            {active === "imagerie" && <ImagerieView />}
            {active === "patients" && <PatientsView />}
            {active === "enotes" && <ENotesView />}
            {active === "config" && <ConfigurationView />}
            {active === "superadmin" && <SuperAdminView onOpenCenter={(id) => setActive(id)} />}
            {active === "ministry-command-center" && <MinistryCommandCenter />}
            {active === "clinicalops" && <ClinicalOpsCenterView onBack={() => setActive("superadmin")} onOpenSub={(id) => setActive(id)} />}
            {active === "operations" && <OperationsCenterView onBack={() => setActive("superadmin")} />}
            {active === "governance" && <PlatformGovernanceCenterView onBack={() => setActive("superadmin")} onOpenSub={(id) => setActive(id)} />}
            {active === "security" && <SecurityOpsCenterView onBack={() => setActive("superadmin")} />}
            {active === "postgres" && <PostgresMetricsView onBack={() => setActive("governance")} />}
            {active === "kubernetes" && <KubernetesMetricsView onBack={() => setActive("governance")} />}
            {active === "redis" && <RedisMetricsView onBack={() => setActive("governance")} />}
            {active === "kafka" && <KafkaMetricsView onBack={() => setActive("governance")} />}
            {active === "doctors" && <DoctorsView onBack={() => setActive("governance")} />}
            {active === "identity-access" && <IdentityAccessView onBack={() => setActive("governance")} />}
            {active === "neuro-command-center" && <NeuroCommandCenter />}
            {active === "cotations" && <CotationsView onBack={() => setActive("governance")} />}
            {active === "machines" && <MachinesView onBack={() => setActive("governance")} />}
            {active === "consultations-atomiques" && <ConsultationsAtomiquesView onBack={() => setActive("governance")} />}
            {active === "pharmacy-stocks" && <PharmacyView onBack={() => setActive("clinicalops")} initialTab="stocks" />}
            {active === "pharmacy-dispensation" && <PharmacyView onBack={() => setActive("clinicalops")} initialTab="dispensation" />}
            {active === "pharmacy-tracabilite" && <PharmacyView onBack={() => setActive("clinicalops")} initialTab="tracabilite" />}
            {active === "pharmacy-vue-patient" && <PharmacyView onBack={() => setActive("clinicalops")} initialTab="vue-patient" />}
            {!["dashboard", "agenda", "laboratoire", "imagerie", "patients", "enotes", "config", "superadmin", "clinicalops", "operations", "governance", "security", "postgres", "kubernetes", "redis", "kafka", "doctors", "cotations", "machines", "consultations-atomiques", "pharmacy-stocks", "pharmacy-dispensation", "pharmacy-tracabilite", "pharmacy-vue-patient", "identity-access", "neuro-command-center", "ministry-command-center"].includes(active) && (
              <ComingSoon label={(TITLES[active] ?? TITLES.dashboard).title} />
            )}
          </section>
        </div>

        <footer className="mt-6 text-center text-xs text-muted-foreground">
          © {new Date().getFullYear()} DikDoc — Tableau de bord clinique
        </footer>
      </div>
    </main>
  );
}
